/**
 * @fileoverview Adding a plant, or changing one: the same drop-downs in the same order.
 *
 * Opened from the Plants tab, from an empty location's "Add a plant" (with
 * that location already chosen), and from Seasons on day one.
 *
 * Adding asks "Planted yet?" where the stage used to be an optional list
 * defaulting to "Not sure": "Not yet" records Planning, from today; "Already
 * growing" shows the stage list (without Planning) and the day it began.
 * Today is the garden's own day (GET /garden-place, useGardenPostcode: the
 * server's, from the garden's time zone, never the phone's; audit A31).
 * One of them must be chosen, and with Already growing a stage. A plant
 * added with no stage is "Not sure", and its card would ask about it;
 * asking here settles it at once.
 *
 * Edit is the plant card's name row (B136); Archive, Delete and, for a plant with twins, Merge into… are in this form's
 * Archive panel (ArchiveOrDeleteDialog), not on the card.
 * The foot is an ActionRow (B111): quiet Archive… far left (edit only), Cancel, then Save or Add to garden, which shows Button's
 * loading spinner while the save waits.
 *
 * Where: PlantGrid (Add a plant, and Edit from a card), PlantQuickLook,
 * LocationGrid (an empty location's "Add a plant", the location chosen),
 * SeasonPage (adding on day one), useAddPlant, and Today's type door
 * (useDoorAddForm), where it stays mounted closed.
 * The garden's plant list (PlantsContext) is read only when editing (the
 * plant's fresh copy): adding reads none, so the closed add form on Today asks
 * for no list (W24 Job C; Today's provider reads it on demand), and the
 * Archive or delete panel, which reads it too, is drawn only when editing. The open
 * form's PlantIdentityFields reads no plant list ("What you grow" is the server's crops answer).
 * API: GET /locations?short=1 (the beds' ids and names offered, W24 Job H; a plant's own archived bed is kept); the plant picker (W24 Job B):
 * Edit asks the plant's own type's varieties answer as it opens (GET /plants/picker/varieties?plantId=, which names its
 * crop), and the fields ask the crops and types answers as they draw them; the caller's onSave (POST /plants/obj/add or
 * POST /plants/update, through PlantsContext) and onRemove (PATCH
 * /plants/:id/status); POST /stage-marks/:plantObjId (a new or changed
 * stage); DELETE /plants/:id { confirm: 'DELETE' } (Delete for good, in ArchiveOrDeleteDialog);
 * GET /notes/:plantObjId (the Notes row).
 * Settings: PLANT_FORM_TIP_TOAST_MS
 * (config/tunables/plantList); the tip list's after_action tip for the
 * plant_added moment.
 * Words (en/es): sitewide.saving (Button's loading word while it saves), Cancel says plants.form.cancel (the form's own word, passed as cancelLabel), plants.form.* (titles, fields.*, missing.*, remove*,
 * save,
 * saveFailed, stageNotSaved, notesTitle, back), plants.stage.*
 * (label, none, failed, sinceNote, sinceEstimate, sincePhoto),
 * plants.notes.heading, plants.notes.addFirst, plantingDates.plantedYet,
 * plantingDates.notYet, plantingDates.alreadyGrowing,
 * PlantIdentityFields', ArchiveOrDeleteDialog's
 * and NotesSection's own (the archive and delete words live there).
 * Watch out: the plant is saved first and its stage second, so a failed
 * stage leaves a saved plant and says so (plants.form.stageNotSaved). A
 * new plant's stage is recorded only if onSave resolves with its id.
 * A failure on our side (D31, lib/serverFailure) says the standard sentence,
 * reported to Sentry, with the support address as a link: in the save,
 * remove and delete errors (the context's sentence is kept, errorSentence)
 * and in the stage toast, in place of "Try again".
 *
 * @module PlantFormDialog
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { Pill } from '@/shared/components/ui/Pill';
import { Label } from '@/shared/components/ui/label';
import { NativeSelect } from '@/shared/components/ui/native-select';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useBedNames } from '@/shared/hooks/useBedNames';
import type { PlantWritePayload } from '@/shared/api/plantCatalogue';
import type { Plant } from '@/shared/types/plant';
import { PlantIdentityFields } from '@/shared/components/plant-fields/PlantIdentityFields';
import {
  EMPTY_CHOICE, missingField, useTypeVarieties, type CropChoice,
} from '@/shared/components/plant-fields/usePlantPicker';
import { NotesSection } from '../NotesSection';
import { getSinglePlant } from '@/shared/api/plants';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { useStagesContext } from '@/shared/hooks/useStagesContext';
import { selectableStages } from '@/shared/lib/stages';
import { ArchiveOrDeleteDialog } from '../ArchiveOrDeleteDialog';
import { addStageMark } from '@/shared/api/stageMarks';
import { getNotes, journalNotes } from '@/shared/api/notes';
import { DateTime, formatDate, parseDate, fromServerStamp } from '@/shared/utils/dateUtils';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { toast } from '@/shared/components/ui/use-toast';
import { ToastAction } from '@/shared/components/ui/toast';
import { cn } from '@/lib/utils';
import { useTips } from '@/features/tips/useTips';
import { useTipAction } from '@/features/tips/useTipAction';
import { PLANT_FORM_TIP_TOAST_MS } from '@/config/tunables/plantList';
import { errorSentence } from '@/shared/lib/errorMessage';
import { serverFailureText } from '@/shared/lib/serverFailure';
import { FailureText } from '@/shared/components/ServerFailure';
import { plantShown } from '@/shared/lib/plantName';
import { canOfferMerge } from '../../lib/mergePlants';

/** A row of the form: one fact about the plant, tapped to open it. */
const ROW = 'flex min-h-12 w-full items-center gap-3 rounded-md border bg-background px-3 py-2 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** The answer to "Planted yet?" when adding: not yet (Planning), or already growing (a stage chosen). */
type PlantedAnswer = 'notYet' | 'growing';

/** The plant to edit (or none, to add), and what saving and removing do. */
interface PlantFormDialogProps {
  /** The plant being edited, or null to add a new one. */
  plant: Plant | null;
  /** Whether the dialog is showing. */
  isOpen: boolean;
  /** Closes the dialog. */
  onClose: () => void;
  /**
   * Saves the plant. Resolves once the list has been refreshed, with the new
   * plant's id when one was added, so its first stage can be recorded.
   */
  onSave: (payload: PlantWritePayload, plantObjId: number | null) => Promise<number | null | void>;
  /** Takes the plant out of the garden. Absent when adding. */
  onRemove?: (plantObjId: number) => Promise<void>;
  /** For a new plant: the location already chosen, when opened from one. */
  initialLocationId?: number | null;
  /** Merge into… (B136), offered in the Archive panel for a plant with twins: the caller closes the form and opens the merge sheet. */
  onMerge?: (plant: Plant) => void;
}

/**
 * Add a plant, or change one already in the garden: one form of standard
 * drop-downs.
 *
 * Plant, then Type and Variety, then Location, then Stage and the day it
 * began (when adding, "Planted yet?" first). The same fields in the same
 * order for adding and editing, and for filing photos from the tray; Edit is
 * simply the form filled in. It replaced
 * a full-screen chooser with chips, counts, a garden list and a second step,
 * which had to be learned before it could be answered.
 *
 * A stage is always a stage on a day. Adding with one records the plant's
 * first stage, which is its start date; editing records a new stage only when
 * it is changed, and the earlier one stays in the plant's history.
 *
 * @param props - PlantFormDialogProps.
 */
export const PlantFormDialog: React.FC<PlantFormDialogProps> = ({
  plant: given, isOpen, onClose, onSave, onRemove, initialLocationId = null, onMerge,
}) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const isNew = given === null;
  const garden = usePlantsContext();
  const { updateSinglePlant } = garden;
  // The garden's copy, which a saved stage refreshes; the form was handed the plant as it was when it opened. The list
  // is read only when editing: on a page that reads it on demand (Today), reading it is what downloads it (W24 Job C).
  const plant = given === null ? null : garden.plants?.find((p) => p.plant_obj_id === given.plant_obj_id) ?? given;
  const { stages: allStages } = useStagesContext();
  // Planning too when editing: a plant not yet sown is Planning, as its card
  // says. Adding offers it as "Not yet" instead, so the list is for growing plants.
  const stages = selectableStages(allStages, { planning: !isNew }) ?? [];
  /** The Planning stage, which "Not yet" records. */
  const planningId = (allStages ?? []).find((stage) => stage.stage_type === 'PLANNING')?.stage_id ?? null;
  /*
   * Edit: the plant's own type's varieties answer, asked straight from the plant as the form opens (it names the crop,
   * which the effect below sets); the server's draw runs no effects, so this is asked before the choice is filled.
   */
  const ownType = useTypeVarieties(isOpen && given ? given.plant?.id ?? null : null);
  const tips = useTips();
  const runTip = useTipAction();
  /** The garden's own day, the server's (from the garden's time zone, never the phone's; audit A31). */
  const { date: today } = useGardenPostcode();

  /** The plant, type and variety chosen. */
  const [choice, setChoice] = React.useState<CropChoice>(EMPTY_CHOICE);
  /** The location chosen. */
  const [locationId, setLocationId] = React.useState<number | null>(null);
  /** The stage chosen (Planning, when adding with "Not yet"). */
  const [stageId, setStageId] = React.useState<number | null>(null);
  /** The answer to "Planted yet?", when adding. */
  const [planted, setPlanted] = React.useState<PlantedAnswer | null>(null);
  /** The day the chosen stage began (YYYY-MM-DD); today unless changed. */
  const [since, setSince] = React.useState(today);
  /** Save has been pressed once, so missing answers are now marked. */
  const [tried, setTried] = React.useState(false);
  /** True while the save is under way. */
  const [isSaving, setIsSaving] = React.useState(false);
  /*
   * What is on screen. The journal, which grows with every note, is a screen
   * of its own behind the Notes row, and archiving or deleting is the shared dialog over
   * the form, from the bottom on a phone.
   */
  const [view, setView] = React.useState<'form' | 'notes'>('form');
  /** The Archive or delete dialog is open. */
  const [panelOpen, setPanelOpen] = React.useState(false);
  /** Why saving failed, said over the foot. */
  const [error, setError] = React.useState<string | null>(null);

  // Fetched rather than taken from LocationsContext, which is not mounted on
  // the plants route — reaching for it threw and took the whole page down.
  // The beds in the garden (status 1): an archived bed is not offered, so a plant cannot be filed into one by
  // accident (owner, 2026-10-16: a summer squash in the archived Squash Bed went on asking for its cover). A plant
  // already in a bed archived since still shows that bed, so editing it does not move it unasked.
  const { data: inGarden = [] } = useBedNames(isOpen);
  /** The beds offered: those in the garden, plus the plant's own bed when it has been archived since. */
  const locations = React.useMemo(() => {
    const own = given?.location;
    return own?.id && !inGarden.some((location) => location.id === own.id)
      ? [...inGarden, { id: own.id, name: own.name }]
      : inGarden;
  }, [inGarden, given?.location]);

  /** The plant's stage now, as the server sends it (with where it came from), or null when adding or none is recorded. */
  const current = plant?.stage ?? null;

  /*
   * Refill whenever the dialog opens, so a cancelled edit leaves nothing
   * behind — and only then. Keyed on the plant's id, not the plant object,
   * which is rebuilt every time the garden refetches.
   */
  const plantObjId = plant?.plant_obj_id ?? null;
  React.useEffect(() => {
    if (!isOpen) return;
    // A plant saved Type-not-sure reads back as Type "Not sure" (B52); the effect below sets its crop from the server's answer.
    setChoice(given ? { cropId: null, plantId: given.plant?.id ?? null, varietyId: given.variety?.id ?? null, typeUnsure: Number(given.type_unsure ?? 0) === 1 } : EMPTY_CHOICE);
    setLocationId(given ? given.location?.id ?? null : initialLocationId);
    setStageId(given ? given.stage?.id ?? null : null);
    setPlanted(null);
    setSince(today);
    setTried(false);
    setView('form');
    setPanelOpen(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, plantObjId]);

  // The crop is set once the plant's own type's answer has arrived (it names the crop); a Type-not-sure plant is then
  // the crop and the flag, with no type and no variety (B52).
  React.useEffect(() => {
    const own = ownType.data;
    if (!isOpen || choice.cropId !== null || choice.plantId === null || !own || own.plant.id !== choice.plantId) return;
    setChoice(choice.typeUnsure
      ? { cropId: own.crop.id, plantId: null, varietyId: null, typeUnsure: true }
      : { cropId: own.crop.id, plantId: choice.plantId, varietyId: choice.varietyId });
  }, [isOpen, choice, ownType.data]);
  /** The chosen type's varieties answer: whether Variety still needs an answer (the fields read the same). */
  const chosenType = useTypeVarieties(choice.plantId);

  // A garden with one location has nothing to ask.
  React.useEffect(() => {
    if (!isOpen || locationId !== null || locations.length !== 1) return;
    setLocationId(locations[0].id);
  }, [isOpen, locationId, locations]);

  /*
   * The stage: recorded only when it says something new. Adding with a stage
   * records the plant's first; editing records one only when it differs from
   * where the plant is now.
   */
  const stageChanged = stageId !== null && (isNew || stageId !== (current?.id ?? null));
  const missing = missingField(choice, chosenType.data?.plant.id === choice.plantId ? chosenType.data : undefined);
  // Type "Not sure" while the plant's crop is not yet known (an Edit form just opened): Save waits, so a chosen type is
  // never filed in its place (B52).
  const cropPending = Boolean(choice.typeUnsure) && choice.cropId === null;
  const showMissing = tried ? missing : null;
  const locationMissing = tried && locationId === null;
  // Adding: "Planted yet?" must be answered, and Already growing needs its stage.
  const plantedMissing = isNew && planted === null;
  const growingStageMissing = isNew && planted === 'growing' && stageId === null;

  /**
   * Answers "Planted yet?": Not yet is Planning from today; Already growing
   * clears the stage for the gardener to choose.
   *
   * @param answer - The answer.
   */
  const choosePlanted = (answer: PlantedAnswer): void => {
    setPlanted(answer);
    setStageId(answer === 'notYet' ? planningId : null);
    setSince(today);
  };

  /**
   * What is read after the form has closed (B73), so the gardener is not kept waiting on it: the plant's card read
   * again when its stage was saved, and, for a new plant, the tip the server placed for this moment, said with the
   * plant's name as every screen says it (the server's names.full). Never throws: a failed read leaves the list as the
   * garden's refetch brings it.
   *
   * @param id - The plant (plant_obj_id).
   * @param stageSaved - A stage mark was saved for it.
   * @param tip - The after-add tip read when saving, or null for none.
   */
  const settleBehind = async (id: number, stageSaved: boolean, tip: ReturnType<typeof tips.read>): Promise<void> => {
    if (stageSaved) await updateSinglePlant(String(id)).catch(() => undefined);
    if (!tip) return;
    // What to say now is the advice the server placed for this moment: by
    // default, that a plant with no photograph cannot be shared, and an offer of one.
    const plantName = await getSinglePlant(String(id)).then((saved) => saved.names.full).catch(() => '');
    const bed = locations.find((location) => location.id === locationId)?.name ?? '';
    const words = tips.words(tip, { plant: plantName, location: bed });
    tips.mark(tip, 'seen');
    toast({
      title: words.title || words.body,
      description: words.title ? words.body : undefined,
      duration: PLANT_FORM_TIP_TOAST_MS,
      action: words.button ? (
        <ToastAction altText={words.button} onClick={() => runTip(tip, { plantObjId: id })}>
          {words.button}
        </ToastAction>
      ) : undefined,
    });
  };

  /**
   * Saves the plant, then its stage if that changed, then closes the form (B73); the tip for
   * a newly added plant and the plant's card are read behind it (settleBehind). A missing answer sends focus to its field instead.
   */
  const handleSave = async (): Promise<void> => {
    if (cropPending) return;
    setTried(true);
    if (missing || locationId === null || (choice.varietyId === null && !choice.typeUnsure) || plantedMissing || growingStageMissing) {
      const field = missing ? `plant-form-${missing}`
        : locationId === null ? 'plant-form-location'
          : plantedMissing ? 'plant-form-planted' : 'plant-form-stage';
      document.getElementById(field)?.focus();
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const saved = await onSave(
        // No status: an edit changes only what it sends, so the plant keeps
        // the status it has, not one this form read some time ago.
        // Type "Not sure" (B52) sends the crop and the flag, never a type: the server files the crop's usual one.
        choice.typeUnsure
          ? { cropId: Number(choice.cropId), typeUnsure: true, locationId }
          : { varietyId: choice.varietyId as number, locationId },
        plant?.plant_obj_id ?? null
      );
      const id = plant?.plant_obj_id ?? (typeof saved === 'number' ? saved : null);
      /** The stage mark is saved (so the plant's card is read again once the form has closed). */
      let stageSaved = false;
      if (stageChanged && id !== null) {
        try {
          await addStageMark(id, stageId as number, since || today);
          stageSaved = true;
        } catch (err) {
          // The plant is saved; say plainly that its stage is not.
          toast({
            title: t('plants.form.stageNotSaved'),
            // A failure on our side says the standard sentence, not "Try again".
            description: serverFailureText(err, 'plantForm.stage')
              ?? t('plants.stage.failed'),
            variant: 'destructive',
          });
        }
      }
      // B73: the plant (and its stage, if it needed one) is saved, so the form closes now; what is left is read behind it.
      if (isNew && id !== null) {
        // addPlant already ran gardenWritten (the brief is being read again): not invalidated a second time (C81).
        tips.happened('plant_added', { gardenWritten: true });
      }
      const tip = isNew && id !== null ? tips.read('after_add') : null;
      onClose();
      if (id !== null) void settleBehind(id, stageSaved, tip);
    } catch (err) {
      setError(errorSentence(err, t('plants.form.saveFailed')));
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * A stage's day, short and in the reader's language ("Jul 10, 2026").
   *
   * @param day - The day (YYYY-MM-DD), if there is one.
   * @returns Its words, or '' for none.
   */
  const stageDate = (day: string | undefined): string => (day
    ? DateTime.fromISO(day).setLocale(language).toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' })
    : '');
  /** The line under Stage: what saving will record, or since when it has been so. */
  const stageHint = isNew
    ? (stageChanged && planted === 'growing' ? t('plants.form.fields.stageStart') : null)
    : stageChanged && current
      ? t('plants.form.fields.stageChanged', { stage: current.name })
      : current
        ? t(current.from === 'estimate' ? 'plants.stage.sinceEstimate' : current.from === 'photo' ? 'plants.stage.sincePhoto' : 'plants.stage.sinceNote', { date: stageDate(current.since) })
        : null;

  /*
   * The journal's row: how many notes, and the latest. The same query the
   * notes screen reads, so a note written there is here on the way back.
   * Photo notes are the picture's, not the plant's, and are left out as the
   * card leaves them out.
   */
  const { data: allNotes = [] } = useQuery({
    queryKey: ['notes', 'plant', plantObjId],
    queryFn: () => getNotes(plantObjId as number),
    initialData: plant?.notes,
    enabled: isOpen && plantObjId !== null,
  });
  /** The plant's own notes, newest first. */
  const journal = React.useMemo(() => journalNotes(allNotes), [allNotes]);
  const latest = journal[0] ?? null;

  /** The Notes row: how many, and the latest, or an offer to write the first. */
  const notesRow = (
    <button type="button" onClick={() => setView('notes')} className={ROW}>
      <span className="min-w-0 grow">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {t('plants.notes.heading')}
          {journal.length > 0 && (
            <Pill size="sm" className="tabular-nums">{journal.length}</Pill>
          )}
        </span>
        {latest ? (
          <>
            <span className="block truncate text-[0.9375rem] font-medium">{latest.content}</span>
            <span className="block truncate text-xs text-muted-foreground">{formatDate(fromServerStamp(latest.timestamp) ?? parseDate(latest.timestamp))}</span>
          </>
        ) : (
          <span className="block truncate text-[0.9375rem] text-muted-foreground">{t('plants.notes.addFirst')}</span>
        )}
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </button>
  );

  /** The plant's name as every screen says it (the server's names; a Type-not-sure plant is its crop alone), for the notes screen's title. */
  const plantName = plant ? plantShown(plant) : '';

  /** Archive or delete, asked by the one shared dialog over the form. */
  const removePanel = (
    <ArchiveOrDeleteDialog
      plant={panelOpen ? plant : null}
      onClose={() => setPanelOpen(false)}
      archive={async (id) => { if (onRemove) await onRemove(id); }}
      photos={plant?.photos?.length ?? 0}
      // Every note goes, the photos' own included.
      notes={allNotes.length}
      onDone={onClose}
      onMerge={onMerge && plant && canOfferMerge(plant, { archived: false, hasHandler: true }) ? onMerge : undefined}
    />
  );

  /** The notes screen is showing (only ever for a plant being edited). */
  const onNotes = view === 'notes' && plant !== null;

  return (
    // Not closed by Escape or a tap outside while a save, removal or delete is
    // under way: a failure would be said in a dialog no longer there.
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isSaving) onClose(); }}>
      {/*
        * The whole screen on a phone, with the button always at the foot; a
        * box of its own size from sm up.
        */}
      <DialogContent
        scrollBody={false}
        onEscapeKeyDown={(e) => { if (onNotes) { e.preventDefault(); setView('form'); } }}
        className={cn(
          'flex h-[100dvh] max-h-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0',
          'sm:max-h-[90dvh] sm:max-w-lg sm:rounded-lg sm:border',
          onNotes ? 'sm:h-[min(90dvh,640px)]' : 'sm:h-auto'
        )}
      >
        {onNotes ? (
          <>
            <DialogHeader className="flex min-h-[3.5rem] shrink-0 flex-row items-center gap-1 space-y-0 border-b py-2 pl-2 pr-14 text-left">
              <button
                type="button"
                onClick={() => setView('form')}
                aria-label={t('plants.form.back')}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <DialogTitle className="truncate">
                {t('plants.form.notesTitle', { name: plantName })}
              </DialogTitle>
            </DialogHeader>
            <div className="min-h-0 flex-1 p-4">
              <NotesSection
                plantObjId={plant.plant_obj_id}
                initialNotes={plant.notes}
                screen
              />
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="flex min-h-[3.5rem] shrink-0 justify-center border-b px-4 py-2 pr-14 text-left">
              <DialogTitle>
                {isNew ? t('plants.form.addTitle') : t('plants.form.editTitle')}
              </DialogTitle>
            </DialogHeader>

            {/*
              * Standard drop-downs, top to bottom: what, where, and how far
              * along. Scrolls only if the type is made very large.
              */}
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-4 pt-3">
              <PlantIdentityFields
                value={choice}
                onChange={(next) => { setChoice(next); }}
                missing={showMissing}
                disabled={isSaving}
                idPrefix="plant-form"
                search={isNew}
              />

              <div>
                <Label className="block" htmlFor="plant-form-location">{t('plants.form.location')}</Label>
                <NativeSelect
                  id="plant-form-location"
                  className="mt-1.5"
                  value={locationId ?? ''}
                  onChange={(event) => setLocationId(event.target.value ? Number(event.target.value) : null)}
                  disabled={isSaving}
                  invalid={locationMissing}
                >
                  {locations.length !== 1 && <option value="">{t('plants.form.pickLocation')}</option>}
                  {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
                </NativeSelect>
                {locationMissing && <p className="mt-1 text-sm font-medium text-destructive">{t('plants.form.missing.location')}</p>}
              </div>

              {/* Adding: planted yet? Not yet is Planning; Already growing asks its stage. */}
              {isNew && (
                <fieldset>
                  <legend className="text-sm font-medium leading-none">{t('plantingDates.plantedYet')}</legend>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    {(['notYet', 'growing'] as const).map((answer) => (
                      <label
                        key={answer}
                        className={cn(
                          'flex min-h-12 cursor-pointer items-center justify-center rounded-md border bg-background px-3 text-center text-[0.9375rem] font-medium focus-within:ring-2 focus-within:ring-ring hover:bg-muted',
                          planted === answer && 'border-2 border-primary bg-primary/10',
                          tried && plantedMissing && 'border-2 border-destructive'
                        )}
                      >
                        <input
                          type="radio"
                          name="plant-form-planted"
                          id={answer === 'notYet' ? 'plant-form-planted' : undefined}
                          className="sr-only"
                          checked={planted === answer}
                          onChange={() => choosePlanted(answer)}
                          disabled={isSaving}
                        />
                        {answer === 'notYet' ? t('plantingDates.notYet') : t('plantingDates.alreadyGrowing')}
                      </label>
                    ))}
                  </div>
                  {tried && plantedMissing && <p className="mt-1 text-sm font-medium text-destructive">{t('plants.form.missing.plantedYet')}</p>}
                </fieldset>
              )}

              {(!isNew || planted === 'growing') && (
                <div>
                  <div className={cn('grid gap-3', stageChanged ? 'grid-cols-2' : 'grid-cols-1')}>
                    <div className="min-w-0">
                      <Label className="block" htmlFor="plant-form-stage">
                        {t('plants.stage.label')}
                      </Label>
                      <NativeSelect
                        id="plant-form-stage"
                        className="mt-1.5"
                        value={stageId ?? ''}
                        onChange={(event) => { setStageId(event.target.value ? Number(event.target.value) : null); setSince(today); }}
                        disabled={isSaving}
                        invalid={tried && growingStageMissing}
                        aria-describedby={stageHint ? 'plant-form-stage-hint' : undefined}
                      >
                        {(isNew || !current) && <option value="">{isNew ? t('plants.form.fields.chooseStage') : t('plants.stage.none')}</option>}
                        {stages.map((stage) => <option key={stage.stage_id} value={stage.stage_id}>{stage.name}</option>)}
                      </NativeSelect>
                    </div>
                    {stageChanged && (
                      <div className="min-w-0">
                        <Label className="block" htmlFor="plant-form-since">{t('plants.form.fields.since')}</Label>
                        <input
                          id="plant-form-since"
                          type="date"
                          value={since}
                          max={today || undefined}
                          onChange={(event) => setSince(event.target.value || today)}
                          disabled={isSaving}
                          className="mt-1.5 block h-12 w-full rounded-md border bg-background px-3 text-base text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </div>
                    )}
                  </div>
                  {tried && growingStageMissing && <p className="mt-1 text-sm font-medium text-destructive">{t('plants.form.missing.stage')}</p>}
                  {stageHint && <p id="plant-form-stage-hint" className="mt-1.5 text-sm text-muted-foreground">{stageHint}</p>}
                </div>
              )}

              {!isNew && plant && notesRow}

              {error && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span><FailureText text={error} /></span>
                </div>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2 border-t px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:justify-end">
              <div className="w-full min-w-0">
                <ActionRow
                  onCancel={onClose}
                  cancelLabel={t('plants.form.cancel')}
                  disabled={isSaving}
                  onRemove={!isNew && onRemove ? () => setPanelOpen(true) : undefined}
                  removeLabel={t('plants.form.removeShort')}
                >
                  <Button onClick={() => { void handleSave(); }} loading={isSaving} disabled={isSaving || cropPending}>
                    {isNew ? t('plants.form.addAction') : t('plants.form.save')}
                  </Button>
                </ActionRow>
              </div>
            </div>
          </>
        )}
      </DialogContent>
      {/* Only for a plant being edited (Remove shows only then): the panel reads the garden's list, which an add form
          on Today must not ask for while it sits closed (W24 Job C). */}
      {!isNew && removePanel}
    </Dialog>
  );
};
